// React
import PropTypes from "prop-types";
import { useEffect, useState } from "react";

// @tanstack/react-query
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

// MUI
import { Box, Grid, Paper, Skeleton, Stack, Typography } from "@mui/material";

// Third-party
import moment from "moment";

// Custom Hooks
import { useAuthorization } from "@/Common/hooks/Authorization/Authorization";

// Contexts
import { useSubscriptions } from "@/Common/context/subscriptions/Subscriptions";
import { useMotivators } from "@/Motivators/context/Common/MotivatorsContext";
import { MotivatorsSectionProvider, useSectionMotivators } from "@/Motivators/context/Common/MotivatorsSectionContext";
import { ManagedSpecialUnitsProvider } from "@/Motivators/context/SpecialUnits/ManagedContext";

// Constants
import actions from "@/Constants/actionsList";
import { DATE_FORMAT } from "@/Constants/date";

// Layouts
import DefaultTiersLoader from "@/Layouts/components/Skeletons/DefaultTiersLoader/DefaultTiersLoader";
import TabLoader from "@/Layouts/components/Skeletons/TabLoader";
import EmptyBody from "@Layouts/components/Basics/EmptyBody";
import RoundedBox from "@Layouts/components/Basics/RoundedBox";
import SmallActionButton from "@Layouts/components/Buttons/SmallActionButton";

// Customs
import NewSpecialList from "@/Motivators/components/SpecialUnits/NewSpecialList";
import SpecialUnitsModelCommissionsContainer from "@/Motivators/components/SpecialUnits/SpecialUnitsModelCommission/SpecialUnitsModelCommissionsContainer";
import ManagedSpecialUnitsTable from "@/Motivators/components/SpecialUnits/Tables/ManagedSpecialUnitsTable";
import SpecialUnitsTable from "@/Motivators/components/SpecialUnits/Tables/SpecialUnitsTable";
import HeaderMotivatorContainer from "@/Motivators/layouts/components/HeaderMotivatorContainer";
import SpiffEntrySectionLoader from "@Motivators/layouts/components/Skeletons/SpiffEntrySectionLoader";

// Helpers
import {
	areAllValidParams,
	canUserEditMotivators,
	createNewItem,
	determineDate,
	getSetupByMotivatorType,
	isNewItem,
	newObjectSetup,
	removeUnsaveItemInTree,
} from "@Motivators/helpers";

// Assets
import empty from "@/assets/images/commons/empty.png";
import { and } from "@/Common/helpers/sonar";
import useCacheCleaner from "@/Common/hooks/CacheCleaner";

const getSubscriptions = (arg) => arg?.subscriptions || null;

const getDataUnist = (arg, selectedItem) => {
	const commissionCycleStartDate = selectedItem?.commissionCycleStartDate;
	return (
		arg?.data?.units?.filter((item) => !item.saleDate || moment(item.saleDate).isSameOrAfter(moment(commissionCycleStartDate))) || []
	);
};

const getDataRules = (arg) => (arg?.data ? arg?.data?.rules : {});

const updateCountMaps = ({ unitId, description, status, descriptionCountMap, statusCountMap }) => {
	const descriptionKey = `${unitId}_${description}`;
	if (descriptionCountMap.has(descriptionKey)) {
		descriptionCountMap.set(descriptionKey, descriptionCountMap.get(descriptionKey) + 1);
	} else {
		descriptionCountMap.set(descriptionKey, 1);
	}

	const statusKey = `${unitId}_${status}`;
	if (statusCountMap.has(statusKey)) {
		statusCountMap.set(statusKey, statusCountMap.get(statusKey) + 1);
	} else {
		statusCountMap.set(statusKey, 1);
	}
};

const getLatestStatusInCommissionCycle = (inventoryItem, commissionCycleEndDate) => {
	const currentStatus = inventoryItem.inventory.status;
	const statusHistory = inventoryItem.inventory.statusHistory;

	if (!statusHistory?.length) {
		return currentStatus;
	}

	const [latestStatusInCommissionCycle] = statusHistory
		.filter((statusEntry) => moment(statusEntry.date).isBefore(moment(commissionCycleEndDate)))
		.sort((a, b) => moment(b.date).diff(moment(a.date)));

	return latestStatusInCommissionCycle?.status ?? currentStatus;
};

const formatSelectionForTable = (specialUnitsList, selectedItem) => {
	const SLICE = -2;
	const transformedData = [];
	if (!specialUnitsList?.units) {
		return [];
	}
	specialUnitsList.units.forEach((unit) => {
		const descriptionCountMap = new Map();
		const statusCountMap = new Map();
		let totalAge = 0;
		let inventoryCount = 0;
		// Extract unit-level data
		const unitData = {
			_id: unit._id,
			hierarchy: unit.hierarchy,
			isParent: unit.isParent,
			search: unit.search,
			criteria: unit.criteria,
			status: unit.status,
			commission: unit.commission,
			createdBy: unit.createdBy,
			createdAt: unit.createdAt,
			lastModifiedOn: unit.lastModifiedOn,
			inventoryListCount: unit.inventoryList?.length,
			descriptionCount: 0,
			statusCount: 0,
			averageAge: 0,
		};
		transformedData.push(unitData);

		// Extract inventory-level data
		if (unit.inventoryList && unit.inventoryList.length > 0) {
			unit?.inventoryList.forEach((inventoryItem) => {
				const latestStatusInPreviousCommissionCycle = getLatestStatusInCommissionCycle(
					inventoryItem,
					selectedItem.commissionCycleStartDate
				);
				if (latestStatusInPreviousCommissionCycle === "SOLD") {
					return;
				}

				const daysDifference = inventoryItem?.inventory?.averageAge ?? 0;
				totalAge += daysDifference;
				inventoryCount++;
				const make = inventoryItem.inventory.properties.find((prop) => prop.displayName === "Make")?.value.trim();
				const year = inventoryItem.inventory.properties
					.find((prop) => prop.displayName === "Year")
					?.value.trim()
					?.slice(SLICE);
				const model = inventoryItem.inventory.properties.find((prop) => prop.displayName === "Model")?.value.trim();
				const trim = inventoryItem.inventory.properties.find((prop) => prop.displayName === "TRIM")?.value.trim();
				const stockId = inventoryItem.inventory.properties.find((prop) => prop.displayName === "Stock ID")?.value.trim();
				const status = getLatestStatusInCommissionCycle(inventoryItem, selectedItem.commissionCycleEndDate);

				const description = `${year},${make},${model},${trim}`;
				const inventoryData = {
					_id: `child_${inventoryItem._id}`,
					hierarchy: inventoryItem.hierarchy,
					isParent: inventoryItem.isParent,
					status: `${status}`,
					stockId: `${stockId}`,
					createdBy: unit.createdBy,
					createdAt: unit.createdAt,
					lastModifiedOn: unit.lastModifiedOn,
					age: daysDifference,
					description,
				};

				updateCountMaps({ unitId: unit._id, description, status, descriptionCountMap, statusCountMap });

				transformedData.push(inventoryData);
			});
		}

		if (inventoryCount > 0) {
			unitData.averageAge = Math.round(totalAge / inventoryCount);
		}

		// Update the description count and status count
		transformedData.forEach((unitDataVal) => {
			if (unitDataVal._id === unit._id) {
				unitDataVal.descriptionCount = Array.from(descriptionCountMap.keys()).filter((key) => key.startsWith(unit._id)).length;
				unitDataVal.statusCount = Array.from(statusCountMap.keys()).filter((key) => key.startsWith(unit._id)).length;
			}
		});
	});

	return transformedData;
};

function ContentRenderer(props) {
	const { locationId, motivatorType, isLoading, setIsLoading, setSelectNewlyCreated, identifiers, setSelection, mutations } =
		useMotivators();

	const [paramsHeader, setParamsHeader] = useState({ title: "New Title", from: null, to: null });
	const { commissionCycle } = useSectionMotivators();
	const { isLoadingData, selectedItem, data, setSelectedItem, hasSpecialEditing } = props;
	const queryClient = useQueryClient();

	const specialUnitsListId = selectedItem?.setupId ?? selectedItem?.specialUnitsListId;

	// Hooks.
	const { useAuth } = useAuthorization();

	// States
	const [loadingDateRange, setLoadingDateRange] = useState({
		from: false,
		to: false,
	});

	const dateRange = {
		from: data?.effectiveDateFrom,
		to: data?.effectiveDateTo,
	};

	// Permissions
	const EDIT_SPECIAL_UNIT_LISTS = useAuth(actions.EDIT_SPECIAL_UNIT_LISTS);
	const MASTER_PERMISSION = canUserEditMotivators(hasSpecialEditing, commissionCycle?.status);

	/****************************************************** MUTATIONS ****************************************************/

	const { mutate: createSpecialUnitsSetup } = useMutation({
		mutationKey: ["createSpecialUnitsSetup"],
		mutationFn: (args) => mutations.createSetup(args),
		onMutate: () => {
			setIsLoading(true);
			props.setOpenNewListDialog(false);
			setSelection((prev) => ({ ...prev, saving: true }));
		},
	});

	const { mutate: mutateTitle } = useMutation({
		mutationKey: ["mutateTitle", locationId, specialUnitsListId],
		mutationFn: (title) => {
			setIsLoading(true);
			return mutations.updateSetupTitle({
				locationId,
				listId: specialUnitsListId,
				value: title,
				specialUnitsListType: selectedItem?.specialUnitsListType,
			});
		},
		onSuccess: () => queryClient.refetchQueries({ queryKey: [`${motivatorType}Sidebar`, locationId] }),
	});

	const { mutate: mutateEffectiveDate } = useMutation({
		mutationKey: ["mutateEffectiveDate", locationId, specialUnitsListId],
		mutationFn: ({ type, dateValue }) => {
			const effectiveDateName = type === "from" ? "effectiveDateFrom" : "effectiveDateTo";
			setLoadingDateRange((load) => ({ ...load, [type]: true }));
			return mutations.updateEffectiveDate({
				locationId,
				effectiveDateName,
				listId: specialUnitsListId,
				commissionCycleId: selectedItem?.commissionCycleId,
				effectiveDateValue: dateValue,
				specialUnitsListType: selectedItem?.specialUnitsListType,
			});
		},
	});

	const { mutate: deleteSpecialUnitSetup } = useMutation({
		mutationKey: ["deleteSpecialUnitSetup", locationId, specialUnitsListId],
		mutationFn: () =>
			mutations.deleteSetup({
				unitListId: specialUnitsListId,
				locationId,
				commissionCycleId: selectedItem.commissionCycleId,
				specialUnitsListType: selectedItem.specialUnitsListType,
			}),
		onMutate: () => setIsLoading(true),
	});

	/**************************************************** SUBSCRIPTIONS ****************************************************/
	const { getSubscription } = useSubscriptions();
	const sidebarSubscriptions = getSubscription([`${motivatorType}Sidebar`, locationId]);

	sidebarSubscriptions?.subscriptions?.onDeleteSetup?.setCallback((result) => {
		setIsLoading(false);
		setSelectedItem(null);
		queryClient.refetchQueries({ queryKey: [`${motivatorType}Sidebar`, locationId] });
	});

	/******************************************************** HANDLERS *****************************************************/

	const handleDeleteSpecialUnitSetup = () => {
		if (!specialUnitsListId) {
			if (isNewItem(selectedItem?._id)) {
				const dataQuery = queryClient.getQueryData([`${motivatorType}Sidebar`, locationId]);
				const newData = removeUnsaveItemInTree(selectedItem.commissionCycleId, dataQuery, identifiers.attribute, motivatorType);
				queryClient.setQueryData([`${motivatorType}Sidebar`, locationId], newData);
				setSelection(null);
			}
			return;
		}
		deleteSpecialUnitSetup();
	};

	const handleAddNewList = async (specialUnitsListType) => {
		if (!locationId) {
			return;
		}

		setParamsHeader({
			from: null,
			to: null,
			title: "New List",
		});

		const dataQuery = queryClient.getQueryData([`${motivatorType}Sidebar`, locationId]);
		if (dataQuery) {
			const newSetup = newObjectSetup(
				identifiers.title,
				motivatorType,
				props.commissionCycleId,
				props.commissionCycleStartDate,
				props.commissionCycleEndDate
			);

			const newData = createNewItem({
				tree: dataQuery,
				ccId: props.commissionCycleId,
				newItem: newSetup,
				param: identifiers.attribute,
			});

			queryClient.setQueryData([`${motivatorType}Sidebar`, locationId], newData);

			const newSetupGeneric = getSetupByMotivatorType(motivatorType, newSetup);
			newSetupGeneric.specialUnitsListType = specialUnitsListType;
			if (props.lastSelected?.current?._id !== newSetupGeneric?._id) {
				const newSetupSelected = { ...newSetupGeneric, _id: `null_${newSetupGeneric._id}` };
				props.lastSelected.current = newSetupSelected;
				setSelection(newSetupSelected);
			}
		}
		setSelectNewlyCreated(true);
		props.setOpenNewListDialog(false);
	};

	const onCloseDialog = () => props.setOpenNewListDialog(false);

	const handleUpdateTitle = (title) => {
		const newParams = { ...paramsHeader, title };
		setParamsHeader(newParams);
		if (!!specialUnitsListId) {
			mutateTitle(title);
		} else if (areAllValidParams(newParams, identifiers.title)) {
			const input = { title: newParams.title, effectiveDateFrom: newParams.from, effectiveDateTo: newParams.to };
			createSpecialUnitsSetup({
				locationId,
				input,
				motivatorType: selectedItem?.specialUnitsListType,
				commissionCycleId: props.commissionCycleId,
			});
		}
	};

	const handleOnUpdateEffectiveDateTime = async (date, type) => {
		const newParams = { ...paramsHeader, [type]: date };
		setParamsHeader(newParams);
		if (!!specialUnitsListId) {
			mutateEffectiveDate({ dateValue: date, type });
		} else if (areAllValidParams(newParams, identifiers.title)) {
			const input = { title: newParams.title, effectiveDateFrom: newParams.from, effectiveDateTo: newParams.to };
			createSpecialUnitsSetup({
				locationId,
				input,
				motivatorType: selectedItem?.specialUnitsListType,
				commissionCycleId: props.commissionCycleId,
			});
		}
	};

	const handleDeleteItems = (itemsSetup, effectiveDate) => {
		const effectiveDateName = effectiveDate.type === "from" ? "effectiveDateFrom" : "effectiveDateTo";
		const effectiveDateValue = effectiveDate.dataTime;
		setIsLoading(true);

		mutations.updateSetup({
			effectiveDateName,
			locationId,
			effectiveDateValue: moment(moment.utc(effectiveDateValue).format(DATE_FORMAT.monthAbbrDatePadded)).startOf("day"),
			items: itemsSetup,
			listId: specialUnitsListId,
		});
	};

	const formatDate = (dateValue) => moment.utc(dateValue).format(DATE_FORMAT.monthAbbrDatePadded);

	const isDeleteSetupDisabled = () => {
		const hasRulesOrItems =
			!!data?.rules?.new?.definitions?.length || !!data?.rules?.preOwned?.definitions?.length || !!data?.units?.length;
		const isAlreadyApplied = moment(formatDate(selectedItem?.effectiveDateFrom)).startOf("day").isBefore(moment(), "day");
		const hasParentCommissionCycle = !!selectedItem?.parentCommissionCycleId;
		return !EDIT_SPECIAL_UNIT_LISTS || isLoading || hasRulesOrItems || isAlreadyApplied || hasParentCommissionCycle;
	};

	useEffect(() => {
		if (selectedItem?.effectiveDateFrom || selectedItem?.effectiveDateTo) {
			setParamsHeader({
				from: determineDate(selectedItem?.effectiveDateFrom),
				to: determineDate(selectedItem?.effectiveDateTo),
				title: selectedItem?.title,
			});
		}
	}, [selectedItem]);

	if (isLoadingData) {
		return (
			<Grid sx={{ position: "relative" }}>
				<RoundedBox component="section" className="mb-6">
					<Box sx={{ display: "flex", justifyContent: "space-between", paddingTop: "5px" }}>
						<Skeleton className="rounded-[6px]" variant="rectangular" width={370} height={30} />
						<Box sx={{ display: "flex", gap: 1 }}>
							<Skeleton className="rounded-[6px]" variant="rectangular" width={170} height={30} />
							<Skeleton className="rounded-[6px]" variant="rectangular" width={170} height={30} />
						</Box>
					</Box>
					<Skeleton className="rounded-[6px]" variant="rectangular" width={160} height={28} sx={{ mt: 6 }} />

					<Stack mt={2} className="-mx-[32px]">
						<TabLoader switch bottomSwitch />
						<DefaultTiersLoader mt={1} buttons />
					</Stack>
				</RoundedBox>
				<RoundedBox component="section">
					<SpiffEntrySectionLoader />
				</RoundedBox>
			</Grid>
		);
	}

	if (!selectedItem) {
		const showNewListDialog = !props.data && props.openNewListDialog;
		return (
			<>
				{showNewListDialog && (
					<NewSpecialList open={Boolean(props.openNewListDialog)} addNewListCallBack={handleAddNewList} onClose={onCloseDialog} />
				)}
				<EmptyBody image={String(empty)} title="No Item selected" bodyHeight="510px" />
			</>
		);
	}

	return (
		<Grid>
			<NewSpecialList open={Boolean(props.openNewListDialog)} addNewListCallBack={handleAddNewList} onClose={onCloseDialog} />
			<Paper className={`!rounded-[10px] bg-white w-[100%] mb-[30px] mt-[40px] !shadow-widget`}>
				<Grid container spacing={5} component={"section"} sx={{ padding: "0 32px" }}>
					<Grid item xs={12} component={"header"}>
						<Stack direction="row" justifyContent={"space-between"} sx={{ marginTop: "-8px" }}>
							<HeaderMotivatorContainer
								dateRange={{ from: paramsHeader.from, to: paramsHeader.to }}
								hasEditPermission={EDIT_SPECIAL_UNIT_LISTS}
								loadingDateRange={loadingDateRange}
								setLoadingDateRange={setLoadingDateRange}
								isLoadingData={isLoadingData}
								title={paramsHeader?.title}
								defaultTitle={identifiers.title}
								onEditTitle={handleUpdateTitle}
								onEditEffectiveDate={handleOnUpdateEffectiveDateTime}
								onDeleteItems={handleDeleteItems}
								toMinDateTime={
									data?.effectiveDateFrom
										? moment(data?.effectiveDateFrom).utc().add(1, "day")
										: moment(selectedItem?.commissionCycleEndDate).utc()
								}
								masterPermission={MASTER_PERMISSION}
							/>
						</Stack>
					</Grid>
				</Grid>
				<SpecialUnitsModelCommissionsContainer
					data={getDataRules(props)}
					loading={props?.isLoading}
					listId={specialUnitsListId}
					motivatorType={motivatorType}
					location={selectedItem.locationId || locationId}
					specialUnitsListType={selectedItem?.specialUnitsListType}
				/>
			</Paper>
			<Box component="section" className="bg-white p-8 rounded-[10px] shadow-widget">
				<Grid container spacing={5}>
					<Grid item xs={12} minHeight="200px" className="!pt-[8px]">
						{selectedItem?.specialUnitsListType === "Manual" ? (
							<Grid className="pt-10">
								<Typography className="page-title" component="div" sx={{ marginLeft: "0px", marginTop: "0px" }}>
									Items
								</Typography>
								<SpecialUnitsTable
									data={getDataUnist(props, selectedItem)}
									dateRange={dateRange}
									subscriptions={getSubscriptions(props)}
									listId={specialUnitsListId}
									locationId={selectedItem.locationId}
									commissionCycleId={selectedItem.commissionCycleId}
									currentModel={props?.data}
									loading={props?.isLoading}
									setDeleteCallBackAction={props.setDeleteCallBackAction}
									setLoadingDateRange={setLoadingDateRange}
									selectedItem={selectedItem}
								/>
							</Grid>
						) : (
							<Grid className="pt-10">
								<Typography className="page-title" component="div" sx={{ marginLeft: "0px", marginTop: "0px" }}>
									Items
								</Typography>
								<Grid m={4} />
								<ManagedSpecialUnitsProvider>
									<ManagedSpecialUnitsTable
										data={formatSelectionForTable(data, selectedItem)}
										dateRange={dateRange}
										commissionCycleId={selectedItem.commissionCycleId}
										listId={specialUnitsListId}
										locationId={selectedItem.locationId ?? locationId}
										currentListId={specialUnitsListId}
										motivatorType={motivatorType}
									/>
								</ManagedSpecialUnitsProvider>
							</Grid>
						)}
					</Grid>
					<Grid xs={12} item alignSelf={"flex-end"} className="absolute right-[50px] bottom-[34px]">
						<SmallActionButton
							disabled={isDeleteSetupDisabled()}
							buttonText="Delete List"
							buttonTextConfirm="Confirm Delete"
							description=""
							onClick={handleDeleteSpecialUnitSetup}
							loading={isLoading}
						/>
					</Grid>
				</Grid>
			</Box>
		</Grid>
	);
}

export default function SpecialUnitsEntrySection({
	commissionCycleId,
	commissionCycleStartDate,
	commissionCycleEndDate,
	openNewListDialog,
	lastSelected,
	setOpenNewListDialog,
	setSelectedItem,
	hasSpecialEditing,
	setDeleteCallBackAction,
}) {
	const { queries, motivatorType, locationId, selection } = useMotivators();
	const QUERY_IDENTIFIERS = [motivatorType + "Entry", "recipientEntry", "managedSpecialUnits"];
	const { clearCache } = useCacheCleaner();

	const specialUnitsListId = selection?.setupId ?? selection?.specialUnitsListId;

	/************************************************ QUERIES *************************************************/
	const { data, isLoading } = useQuery({
		queryKey: [`${motivatorType}Entry`, locationId, specialUnitsListId],
		queryFn: () =>
			queries.getBodyData({
				locationId,
				listId: specialUnitsListId,
				specialUnitsListType: selection?.specialUnitsListType,
			}),
		enabled: and(!!locationId, !!specialUnitsListId),
	});

	useEffect(() => {
		return () => {
			clearCache(QUERY_IDENTIFIERS);
		};
	}, []);

	return (
		<Grid sx={{ width: "100%", height: "auto" }}>
			<MotivatorsSectionProvider>
				<ContentRenderer
					openNewListDialog={openNewListDialog}
					commissionCycleId={commissionCycleId}
					commissionCycleStartDate={commissionCycleStartDate}
					commissionCycleEndDate={commissionCycleEndDate}
					selectedItem={selection}
					setOpenNewListDialog={setOpenNewListDialog}
					setSelectedItem={setSelectedItem}
					data={data}
					isLoadingData={isLoading}
					lastSelected={lastSelected}
					hasSpecialEditing={hasSpecialEditing}
					setDeleteCallBackAction={setDeleteCallBackAction}
				/>
			</MotivatorsSectionProvider>
		</Grid>
	);
}

SpecialUnitsEntrySection.propTypes = {
	selectedItem: PropTypes.shape({
		_id: PropTypes.string,
		title: PropTypes.string,
		startDate: PropTypes.string,
		endDate: PropTypes.string,
		commissionCycleId: PropTypes.string,
		commissionCycleStartDate: PropTypes.string,
		commissionCycleEndDate: PropTypes.string,
		effectiveDateFrom: PropTypes.string,
		effectiveDateTo: PropTypes.string,
		parentCommissionCycleId: PropTypes.string,
		locationId: PropTypes.string,
		specialUnitsListType: PropTypes.string,
	}),
	commissionCycleId: PropTypes.string,
	commissionCycleStartDate: PropTypes.string,
	commissionCycleEndDate: PropTypes.string,
	openNewListDialog: PropTypes.bool,
	lastSelected: PropTypes.shape({
		current: PropTypes.shape({
			_id: PropTypes.string,
		}),
	}),
	setOpenNewListDialog: PropTypes.func,
	setSelectedItem: PropTypes.func,
	hasSpecialEditing: PropTypes.bool,
};

ContentRenderer.propTypes = {
	isLoading: PropTypes.bool,
	isLoadingData: PropTypes.bool,
	commissionCycleId: PropTypes.string,
	openNewListDialog: PropTypes.bool,
	selectedItem: PropTypes.shape({
		_id: PropTypes.string,
		title: PropTypes.string,
		startDate: PropTypes.object,
		endDate: PropTypes.object,
		commissionCycleId: PropTypes.string,
		commissionCycleStartDate: PropTypes.string,
		commissionCycleEndDate: PropTypes.string,
		effectiveDateFrom: PropTypes.string,
		effectiveDateTo: PropTypes.string,
		parentCommissionCycleId: PropTypes.string,
		locationId: PropTypes.string,
		specialUnitsListType: PropTypes.string,
	}),
	setSelectedItem: PropTypes.func,
	setOpenNewListDialog: PropTypes.func,
	setDeleteCallBackAction: PropTypes.func,
	data: PropTypes.shape({
		title: PropTypes.string,
		items: PropTypes.array,
		effectiveDateFrom: PropTypes.string,
		effectiveDateTo: PropTypes.string,
	}),
	commissionCycleStartDate: PropTypes.string,
	commissionCycleEndDate: PropTypes.string,
	lastSelected: PropTypes.shape({
		current: PropTypes.shape({
			_id: PropTypes.string,
		}),
	}),
	hasSpecialEditing: PropTypes.bool,
};
